export interface FmicTaxonomyOption {
  value: string;
  label: string;
}

// Unique top-level categories (the text before the first "."), sorted. A bare entry with no
// "." at all (e.g. "Clothing", "Guitars", "Internal Use") IS its own category — it also
// coexists with real subcategories under the same name, handled by getFmicSubcategories below.
export function getFmicCategories(taxonomies: readonly string[]): string[] {
  const categories = new Set<string>();
  for (const taxonomy of taxonomies) {
    const dot = taxonomy.indexOf(".");
    categories.add(dot === -1 ? taxonomy : taxonomy.slice(0, dot));
  }
  return Array.from(categories).sort();
}

// Every taxonomy value under `category`, as {value, label} pairs ready for a subcategory
// picker. Includes the bare category entry itself (labeled "All <category>") when one exists
// in `taxonomies` alongside its real subcategories (confirmed real cases: Clothing, Guitars,
// Internal Use all have both).
export function getFmicSubcategories(taxonomies: readonly string[], category: string): FmicTaxonomyOption[] {
  return taxonomies
    .filter((taxonomy) => taxonomy === category || taxonomy.startsWith(`${category}.`))
    .map((taxonomy) => ({
      value: taxonomy,
      label: taxonomy === category ? `All ${category}` : taxonomy.slice(category.length + 1),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Map an FMIC taxonomy ("Category.Subcategory", e.g. "Guitars.Electric Guitars") onto the coarse
 * category stored on a missing-serial record. paeapi turns that value into a Fender registration
 * reporting category, so it only needs to land in the right bucket, not be exact.
 *
 * Checked most-specific-first: an acoustic bass is a bass, and "Basses.Acoustic Basses" must not
 * fall through to acoustic_guitars.
 */
export function fmicTaxonomyToGearCategory(taxonomy: string): string {
  const value = (taxonomy || "").toLowerCase();
  if (value.startsWith("basses") || value.includes("bass")) return "bass_guitars";
  if (value.includes("acoustic")) return "acoustic_guitars";
  if (value.startsWith("guitars")) return "electric_guitars";
  if (value.includes("amp")) return "amplifiers";
  return "other";
}
